import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UserGroupIcon, ClockIcon, FlagIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { formatScore as formatScoreUtil, scoreColor as scoreColorUtil, calcRoundScore, type ScoringMethod } from '../../lib/scoring';

interface PlayerInfo {
  id: string;
  first_name: string;
  last_name: string;
  country: string;
  category: string;
  handicap: number;
  resident_club: string;
  tournament_id: string;
}

interface RoundScore {
  round: number;
  score: number;
  date: string | null;
  round_name: string;
  has_score: boolean;
  holes: { hole: number; strokes: number }[];
}

interface PairingGroup {
  round_number: number;
  round_name: string;
  round_date: string | null;
  tee_time: string | null;
  starting_hole: number | null;
  group_number: number;
  players: { id: string; name: string; country: string; handicap: number }[];
}

function scoreBg(score: number, method: ScoringMethod): string {
  if (method === 'stableford_net') {
    if (score >= 36) return 'bg-green-50 border-green-100';
    if (score >= 30) return 'bg-gray-50 border-gray-100';
    return 'bg-red-50 border-red-100';
  }
  if (score < 0) return 'bg-green-50 border-green-100';
  if (score > 0) return 'bg-red-50 border-red-100';
  return 'bg-gray-50 border-gray-100';
}

export default function MobilePlayerDetails() {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const [player, setPlayer] = useState<PlayerInfo | null>(null);
  const [scores, setScores] = useState<RoundScore[]>([]);
  const [pairings, setPairings] = useState<PairingGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalScore, setTotalScore] = useState(0);
  const [position, setPosition] = useState<number | null>(null);
  const [scoringMethod, setScoringMethod] = useState<ScoringMethod>('stroke_play_net');
  const [expandedRounds, setExpandedRounds] = useState<Set<number>>(new Set());
  const [courseParMap, setCourseParMap] = useState<Record<number, number>>({});

  useEffect(() => {
    if (playerId) fetchPlayerData();
  }, [playerId]);

  const fetchPlayerData = async () => {
    try {
      const { data: playerData } = await supabase
        .from('players')
        .select('id, first_name, last_name, country, category, handicap, resident_club, tournament_id')
        .eq('id', playerId!)
        .maybeSingle();

      if (!playerData) return;
      setPlayer(playerData);

      const { data: methodData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', `scoring_method_${playerData.tournament_id}`)
        .maybeSingle();
      const method = (methodData?.value as ScoringMethod) || 'stroke_play_net';
      setScoringMethod(method);

      const { data: disabledData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', `disabled_rounds_${playerData.tournament_id}`)
        .maybeSingle();
      const disabledSet = new Set<number>(
        Array.isArray(disabledData?.value) ? disabledData.value : []
      );

      const [scoresResult, pairingsResult, positionResult] = await Promise.all([
        fetchScores(playerData.id, playerData.tournament_id, playerData.handicap, method),
        fetchPairings(playerData.id, playerData.tournament_id),
        fetchPosition(playerData.id, playerData.tournament_id),
      ]);

      const enabledScores = scoresResult.filter(s => !disabledSet.has(s.round));
      setScores(enabledScores);
      setPairings(pairingsResult);
      setTotalScore(enabledScores.filter(s => s.has_score).reduce((sum, s) => sum + s.score, 0));
      setPosition(positionResult);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const fetchScores = async (pId: string, tId: string, handicap: number, method: ScoringMethod): Promise<RoundScore[]> => {
    const [{ data: rounds }, { data: playerPairingsData }, { data: holeRows }, { data: tournamentCourse }] = await Promise.all([
      supabase
        .from('tournament_rounds')
        .select('id, round_number, name, date')
        .eq('tournament_id', tId)
        .order('sort_order', { ascending: true }),
      supabase
        .from('player_pairings')
        .select('round_id')
        .eq('player_id', pId)
        .eq('tournament_id', tId),
      supabase
        .from('hole_scores')
        .select('round, hole, strokes, score_status')
        .eq('player_id', pId)
        .eq('tournament_id', tId)
        .in('score_status', ['draft', 'submitted', 'verified'])
        .order('round')
        .order('hole'),
      supabase
        .from('tournament_courses')
        .select('course_id')
        .eq('tournament_id', tId)
        .maybeSingle(),
    ]);

    let holeParMap: Record<number, number> = {};
    let holeHcpMap: Record<number, number> = {};
    if (tournamentCourse?.course_id) {
      const { data: holesData } = await supabase
        .from('holes')
        .select('hole_number, par, handicap_index')
        .eq('course_id', tournamentCourse.course_id)
        .order('hole_number');
      if (holesData) {
        for (const h of holesData) {
          holeParMap[h.hole_number] = h.par;
          holeHcpMap[h.hole_number] = h.handicap_index ?? h.hole_number;
        }
      }
    }
    setCourseParMap(holeParMap);

    const pairedRoundIds = new Set(playerPairingsData?.map(p => p.round_id) || []);
    const assignedRounds = (rounds || []).filter(r => pairedRoundIds.has(r.id));

    const holesByRound: Record<number, { hole: number; strokes: number }[]> = {};
    for (const row of (holeRows || [])) {
      if (!holesByRound[row.round]) holesByRound[row.round] = [];
      holesByRound[row.round].push({ hole: row.hole, strokes: row.strokes });
    }

    return assignedRounds.map(r => {
      const holes = holesByRound[r.round_number];
      const hasScore = !!holes && holes.length > 0;
      const score = hasScore
        ? calcRoundScore(holes, handicap, holeParMap, holeHcpMap, method)
        : 0;
      return {
        round: r.round_number,
        score,
        date: r.date || null,
        round_name: r.name || `Round ${r.round_number}`,
        has_score: hasScore,
        holes: hasScore ? holes.sort((a, b) => a.hole - b.hole) : [],
      };
    });
  };

  const fetchPairings = async (pId: string, tId: string): Promise<PairingGroup[]> => {
    const { data: playerPairings } = await supabase
      .from('player_pairings')
      .select('*')
      .eq('player_id', pId)
      .eq('tournament_id', tId);

    if (!playerPairings || playerPairings.length === 0) return [];

    const roundIds = [...new Set(playerPairings.map(p => p.round_id))];

    const { data: rounds } = await supabase
      .from('tournament_rounds')
      .select('id, round_number, name, date, sort_order')
      .in('id', roundIds)
      .order('sort_order', { ascending: true });

    const groups: PairingGroup[] = [];

    for (const pp of playerPairings) {
      const round = rounds?.find(r => r.id === pp.round_id);
      if (!round) continue;

      const { data: groupPairings } = await supabase
        .from('player_pairings')
        .select('player_id')
        .eq('round_id', pp.round_id)
        .eq('group_number', pp.group_number)
        .eq('tournament_id', tId);

      const playerIds = groupPairings?.map(gp => gp.player_id) || [];

      const { data: playersData } = await supabase
        .from('players')
        .select('id, first_name, last_name, country, handicap')
        .in('id', playerIds);

      groups.push({
        round_number: round.round_number,
        round_name: round.name || `Round ${round.round_number}`,
        round_date: round.date || null,
        tee_time: pp.tee_time,
        starting_hole: pp.starting_hole ?? null,
        group_number: pp.group_number,
        players: (playersData || []).map(p => ({
          id: p.id,
          name: `${p.first_name} ${p.last_name}`,
          country: p.country,
          handicap: p.handicap,
        })),
      });
    }

    groups.sort((a, b) => a.round_number - b.round_number);
    return groups;
  };

  const fetchPosition = async (pId: string, tId: string): Promise<number | null> => {
    const { data } = await supabase
      .from('leaderboard_view')
      .select('position')
      .eq('id', pId)
      .eq('tournament_id', tId)
      .gt('rounds_played', 0)
      .maybeSingle();

    return data?.position ?? null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  if (!player) {
    return (
      <div className="text-center py-16 px-4">
        <p className="text-gray-400 text-sm">Player not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-golf-green text-sm font-medium">
          Go back
        </button>
      </div>
    );
  }

  const hasScores = scores.length > 0;

  return (
    <div className="pb-8 min-h-[80vh]">
      {/* Hero header with gradient */}
      <div className="relative bg-gradient-to-br from-golf-green to-golf-green/90 px-4 pt-4 pb-8 rounded-b-3xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-white/70 text-sm mb-5 active:text-white transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-bold text-white">
              {player.first_name[0]}{player.last_name[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-white truncate">
              {player.first_name} {player.last_name}
            </h1>
            <p className="text-sm text-white/70 mt-0.5">
              {player.country} · {player.category}
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="inline-flex items-center gap-1 text-xs text-white/60 bg-white/10 px-2 py-0.5 rounded-full">
                HCP {player.handicap}
              </span>
              <span className="text-xs text-white/60 truncate">{player.resident_club}</span>
            </div>
          </div>
        </div>

        {/* Stats cards floating at bottom of hero */}
        {hasScores && (
          <div className="flex gap-2 mt-5">
            {position && (
              <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2.5 text-center border border-white/10">
                <p className="text-[10px] uppercase tracking-wider text-white/50 font-medium">Position</p>
                <p className="text-lg font-bold text-white mt-0.5">
                  {position}<span className="text-xs font-normal text-white/50">{position === 1 ? 'st' : position === 2 ? 'nd' : position === 3 ? 'rd' : 'th'}</span>
                </p>
              </div>
            )}
            <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2.5 text-center border border-white/10">
              <p className="text-[10px] uppercase tracking-wider text-white/50 font-medium">Total</p>
              <p className={`text-lg font-bold mt-0.5 ${scoringMethod === 'stableford_net' ? (totalScore >= 36 ? 'text-green-300' : totalScore >= 30 ? 'text-white' : 'text-red-300') : (totalScore < 0 ? 'text-green-300' : totalScore > 0 ? 'text-red-300' : 'text-white')}`}>
                {formatScoreUtil(totalScore, scoringMethod)}
              </p>
            </div>
            <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2.5 text-center border border-white/10">
              <p className="text-[10px] uppercase tracking-wider text-white/50 font-medium">Rounds</p>
              <p className="text-lg font-bold text-white mt-0.5">{scores.length}</p>
            </div>
          </div>
        )}
      </div>

      {/* Scores Section */}
      <div className="px-4 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <FlagIcon className="w-4 h-4 text-golf-green" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
            {scoringMethod === 'stableford_net' ? 'Stableford (Net)' : 'Stroke Play (Net)'}
          </h2>
        </div>

        {!hasScores ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-gray-100 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
              <FlagIcon className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-sm text-gray-400 font-medium">No rounds played yet</p>
            <p className="text-xs text-gray-300 mt-1">Scores will appear here once play begins</p>
          </div>
        ) : (
          <div className="space-y-3">
            {scores.map((score) => {
              const isExpanded = expandedRounds.has(score.round);
              const front9 = score.holes.filter(h => h.hole <= 9);
              const back9 = score.holes.filter(h => h.hole > 9);
              const front9Total = front9.reduce((sum, h) => sum + h.strokes, 0);
              const back9Total = back9.reduce((sum, h) => sum + h.strokes, 0);
              const grossTotal = front9Total + back9Total;

              return (
                <div key={score.round} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                  <button
                    onClick={() => {
                      const next = new Set(expandedRounds);
                      if (next.has(score.round)) next.delete(score.round);
                      else next.add(score.round);
                      setExpandedRounds(next);
                    }}
                    className="flex items-center w-full px-4 py-3.5 active:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 text-left">
                      <p className="text-sm font-semibold text-gray-800">{score.round_name}</p>
                      {score.date && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(score.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {score.has_score ? (
                        <div className={`px-3 py-1 rounded-lg border ${scoreBg(score.score, scoringMethod)}`}>
                          <span className={`text-sm font-bold ${scoreColorUtil(score.score, scoringMethod)}`}>
                            {formatScoreUtil(score.score, scoringMethod)}
                          </span>
                        </div>
                      ) : (
                        <div className="px-3 py-1 rounded-lg border border-gray-200 bg-gray-50">
                          <span className="text-sm font-medium text-gray-400">Pending</span>
                        </div>
                      )}
                      {score.has_score && (
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </button>

                  {isExpanded && score.has_score && (
                    <div className="border-t border-gray-100 px-2 pb-3">
                      <div className="flex items-center justify-between px-2 py-2">
                        <span className="text-[10px] text-gray-500 uppercase font-medium">Gross: {grossTotal}</span>
                        <span className={`text-[10px] font-bold ${scoreColorUtil(score.score, scoringMethod)}`}>
                          Net: {formatScoreUtil(score.score, scoringMethod)}
                        </span>
                      </div>

                      {/* Front 9 */}
                      {front9.length > 0 && (
                        <div className="overflow-x-auto mb-2">
                          <table className="w-full text-[10px]">
                            <thead>
                              <tr className="border-b border-gray-100">
                                <th className="px-1 py-1 text-gray-400 font-medium text-center w-7">Hole</th>
                                {front9.map(h => (
                                  <th key={h.hole} className="px-0.5 py-1 text-gray-500 font-medium text-center min-w-[22px]">{h.hole}</th>
                                ))}
                                <th className="px-1 py-1 text-gray-700 font-bold text-center bg-gray-50 min-w-[26px]">Out</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.keys(courseParMap).length > 0 && (
                                <tr className="border-b border-gray-50">
                                  <td className="px-1 py-1 text-gray-400 font-medium text-center">Par</td>
                                  {front9.map(h => (
                                    <td key={h.hole} className="px-0.5 py-1 text-center text-gray-400">{courseParMap[h.hole] || '-'}</td>
                                  ))}
                                  <td className="px-1 py-1 text-center text-gray-500 font-medium bg-gray-50">
                                    {front9.reduce((s, h) => s + (courseParMap[h.hole] || 0), 0)}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td className="px-1 py-1 text-gray-500 font-medium text-center">Score</td>
                                {front9.map(h => {
                                  const par = courseParMap[h.hole];
                                  const diff = par ? h.strokes - par : 0;
                                  const cellColor = diff < 0 ? 'text-green-600 font-bold' : diff > 0 ? 'text-red-600 font-bold' : 'text-gray-800 font-semibold';
                                  return (
                                    <td key={h.hole} className={`px-0.5 py-1 text-center ${cellColor}`}>{h.strokes}</td>
                                  );
                                })}
                                <td className="px-1 py-1 text-center font-bold text-gray-900 bg-gray-50">{front9Total}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Back 9 */}
                      {back9.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-[10px]">
                            <thead>
                              <tr className="border-b border-gray-100">
                                <th className="px-1 py-1 text-gray-400 font-medium text-center w-7">Hole</th>
                                {back9.map(h => (
                                  <th key={h.hole} className="px-0.5 py-1 text-gray-500 font-medium text-center min-w-[22px]">{h.hole}</th>
                                ))}
                                <th className="px-1 py-1 text-gray-700 font-bold text-center bg-gray-50 min-w-[26px]">In</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.keys(courseParMap).length > 0 && (
                                <tr className="border-b border-gray-50">
                                  <td className="px-1 py-1 text-gray-400 font-medium text-center">Par</td>
                                  {back9.map(h => (
                                    <td key={h.hole} className="px-0.5 py-1 text-center text-gray-400">{courseParMap[h.hole] || '-'}</td>
                                  ))}
                                  <td className="px-1 py-1 text-center text-gray-500 font-medium bg-gray-50">
                                    {back9.reduce((s, h) => s + (courseParMap[h.hole] || 0), 0)}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td className="px-1 py-1 text-gray-500 font-medium text-center">Score</td>
                                {back9.map(h => {
                                  const par = courseParMap[h.hole];
                                  const diff = par ? h.strokes - par : 0;
                                  const cellColor = diff < 0 ? 'text-green-600 font-bold' : diff > 0 ? 'text-red-600 font-bold' : 'text-gray-800 font-semibold';
                                  return (
                                    <td key={h.hole} className={`px-0.5 py-1 text-center ${cellColor}`}>{h.strokes}</td>
                                  );
                                })}
                                <td className="px-1 py-1 text-center font-bold text-gray-900 bg-gray-50">{back9Total}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Total */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
              <div className="flex items-center px-4 py-3 bg-gray-50/80">
                <span className="flex-1 text-sm font-bold text-gray-700">Total</span>
                <div className={`px-3 py-1 rounded-lg border ${scoreBg(totalScore, scoringMethod)}`}>
                  <span className={`text-sm font-bold ${scoreColorUtil(totalScore, scoringMethod)}`}>
                    {formatScoreUtil(totalScore, scoringMethod)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pairings Section */}
      <div className="px-4 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <UserGroupIcon className="w-4 h-4 text-golf-green" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Pairings</h2>
        </div>

        {pairings.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-gray-100 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
              <UserGroupIcon className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-sm text-gray-400 font-medium">No pairings assigned yet</p>
            <p className="text-xs text-gray-300 mt-1">Groups will appear once draw is complete</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {pairings.map((group, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-golf-green/5 border-b border-golf-green/10">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-golf-green">{group.round_name}</span>
                    {group.round_date && (
                      <span className="text-[10px] text-gray-500 font-medium">
                        {new Date(group.round_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full font-medium">
                      Group {group.group_number}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {group.starting_hole && (
                      <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        Hole {group.starting_hole}
                      </span>
                    )}
                    {group.tee_time && (
                      <div className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        <ClockIcon className="w-3 h-3" />
                        {group.tee_time}
                      </div>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {group.players.map(p => {
                    const isCurrent = p.id === playerId;
                    return (
                      <div
                        key={p.id}
                        className={`flex items-center px-4 py-3 ${isCurrent ? 'bg-golf-green/5' : ''}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mr-3 ${isCurrent ? 'bg-golf-green' : 'bg-gray-100'}`}>
                          <span className={`text-[10px] font-bold ${isCurrent ? 'text-white' : 'text-gray-500'}`}>
                            {p.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${isCurrent ? 'font-bold text-golf-green' : 'font-medium text-gray-700'}`}>
                            {p.name}
                          </p>
                          <p className="text-xs text-gray-400">{p.country} · HCP {p.handicap}</p>
                        </div>
                        {isCurrent && (
                          <span className="text-[10px] text-golf-green font-semibold bg-golf-green/10 px-2 py-0.5 rounded-full">You</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
